<template>
  <main class="flex-1 relative overflow-y-auto focus:outline-none" tabindex="0">
    <form @submit.prevent="submitRepair">
      <div class="my-6 max-w-1/6 mx-auto sm:px-6 lg:px-8 px-5">
        <div class="md:flex md:items-center md:justify-between">
          <div class="flex min-w-0">
            <svg-vue class="content-title-icon" icon="font-awesome.plus-circle-solid"></svg-vue>
            <h1 class="content-title">
              {{ $t('Create standard repair invoice') }}
            </h1>
          </div>
          <div class="mt-4 flex md:mt-0 md:ml-4">
            <router-link class="btn btn-red shadow-sm rounded-md" to="/admin/repair-orders">
              {{ $t('Back') }}
            </router-link>
          </div>
        </div>
      </div>
      <div class="my-6 max-w-1/6 mx-auto px-4 sm:px-6 lg:px-8">
        <div class="content-card-body">
          <loading :status="loading"></loading>
          <div class="md:grid md:grid-cols-3 md:gap-6 px-4 py-5 sm:p-6">
            <!-- customer information -->
            <template>
              <div class="md:col-span-1">
                <h3 class="form-description-head">{{ $t('Customer Information') }}</h3>
                <p class="form-description-text">{{ $t('Valid customer information required to contact letter') }}.</p>
              </div>
              <div class="mt-5 md:mt-0 md:col-span-2">
                <div class="grid grid-cols-3 gap-3">
                  <div class="col-span-3 md:flex md:space-x-2">
                    <div class="w-full">
                      <label class="form-label" for="name">{{ $t('Name') }}</label>
                      <input :placeholder="$t('Name')" class="form-input" id="name" required v-model="repair.name" />
                    </div>
                    <div class="w-full">
                      <label class="form-label" for="email">{{ $t('Email address') }}</label>
                      <div class="pt-1">
                        <input
                          :placeholder="$t('Email address')"
                          class="form-input"
                          id="email"
                          required
                          type="email"
                          v-model="repair.email"
                        />
                      </div>
                    </div>
                  </div>
                  <div class="col-span-3 md:flex md:space-x-2">
                    <div class="w-full">
                      <label class="form-label" for="phone">{{ $t('Phone Number') }}</label>
                      <div class="pt-1">
                        <input :placeholder="$t('Phone Number')" class="form-input" id="phone" v-model="repair.phone" />
                      </div>
                    </div>
                    <div class="w-full">
                      <label class="form-label" for="serial_number">{{ $t('Serial number') }}</label>
                      <div class="pt-1">
                        <input
                          :placeholder="$t('Serial number')"
                          class="form-input rounded-0"
                          id="serial_number"
                          v-model="repair.serial_number"
                        />
                      </div>
                    </div>
                  </div>
                  <div class="col-span-3">
                    <label class="form-label" for="address">{{ $t('Home address') }}</label>
                    <div class="pt-1">
                      <input :placeholder="$t('Home address')" class="form-input" id="address" v-model="repair.address" />
                    </div>
                  </div>
                  <div class="col-span-3">
                    <label class="form-label" for="diagnostics">{{ $t('Initial diagnostics') }}</label>
                    <div class="pt-1">
                      <input-wysiwyg id="diagnostics" v-model="repair.diagnostics"></input-wysiwyg>
                    </div>
                  </div>
                </div>
              </div>
            </template>
            <!-- customer info ends -->
            <!-- brand list start -->
            <template v-if="brandList.length">
              <div class="md:col-span-3">
                <div class="py-3">
                  <div class="border-t border-gray-200"></div>
                </div>
              </div>
              <div class="md:col-span-1">
                <h3 class="form-description-head">{{ $t('Select your device brand') }}</h3>
                <p class="form-description-text">{{ $t('Brands we repair') }}.</p>
              </div>
              <div class="mt-5 md:mt-0 md:col-span-2">
                <div class="grid grid-cols-3 gap-3">
                  <div class="col-span-3">
                    <div :style="{ maxHeight: '200px' }" class="flex flex-col overflow-y-auto">
                      <template v-for="(brand, index) in brandList">
                        <div
                          :key="index"
                          @click="getDevices(brand)"
                          class="flex items-center px-6 py-3 hover:bg-gray-100 cursor-pointer rounded"
                        >
                          <div>
                            <div class="flex items-center justify-center">
                              <svg-vue
                                class="w-5 h-5 text-green-400"
                                icon="font-awesome.check-circle-solid"
                                v-if="repair.brand_id == brand.id"
                              ></svg-vue>
                              <div class="w-5 h-5 p-1 overflow-hidden rounded-full border" v-else></div>
                            </div>
                          </div>
                          <div class="flex flex-col ml-6">
                            <p class="text-sm leading-5 text-gray-700 group-hover:text-gray-900">
                              {{ brand.name }}
                            </p>
                          </div>
                        </div>
                      </template>
                    </div>
                  </div>
                </div>
              </div>
            </template>
            <!-- brand list end -->
            <!-- device list end -->
            <template v-if="deviceList.length">
              <div class="md:col-span-3">
                <div class="py-3">
                  <div class="border-t border-gray-200"></div>
                </div>
              </div>
              <div class="md:col-span-1">
                <h3 class="form-description-head">{{ $t('Select your device') }}</h3>
                <p class="form-description-text">{{ $t('Available devices for selected brand') }}.</p>
              </div>
              <div class="mt-5 md:mt-0 md:col-span-2">
                <div class="grid grid-cols-3 gap-3">
                  <div class="col-span-3">
                    <div :style="{ maxHeight: '200px' }" class="flex flex-col overflow-y-auto">
                      <template v-for="(device, index) in deviceList">
                        <div
                          :key="index"
                          @click="getDefects(device)"
                          class="flex items-center px-6 py-3 hover:bg-gray-100 cursor-pointer rounded"
                        >
                          <div>
                            <div class="flex items-center justify-center">
                              <svg-vue
                                class="w-5 h-5 text-green-400"
                                icon="font-awesome.check-circle-solid"
                                v-if="repair.device_id == device.id"
                              ></svg-vue>
                              <div class="w-5 h-5 p-1 overflow-hidden rounded-full border" v-else></div>
                            </div>
                          </div>
                          <div class="flex flex-col ml-6">
                            <p class="text-sm leading-5 text-gray-700 group-hover:text-gray-900">
                              {{ device.name }}
                            </p>
                            <p
                              class="text-xs leading-4 text-gray-900 group-hover:text-gray-700 group-focus:underline transition ease-in-out duration-150"
                            >
                              {{ device.model }}
                            </p>
                          </div>
                          <div class="ml-auto">
                            <img :alt="device.name" :src="device.avatar" class="inline-block rounded-full h-8 w-8" />
                          </div>
                        </div>
                      </template>
                    </div>
                  </div>
                </div>
              </div>
            </template>
            <!-- device list end -->
            <!-- defects list start -->
            <template v-if="defectList.length">
              <div class="md:col-span-3">
                <div class="py-3">
                  <div class="border-t border-gray-200"></div>
                </div>
              </div>
              <div class="md:col-span-1">
                <h3 class="form-description-head">{{ $t('Select repairable issues') }}</h3>
                <p class="form-description-text">{{ $t('Available services/defect for selected device') }}.</p>
              </div>
              <div class="mt-5 md:mt-0 md:col-span-2">
                <div class="grid grid-cols-3 gap-3">
                  <div class="col-span-3">
                    <div :style="{ maxHeight: '200px' }" class="flex flex-col overflow-y-auto">
                      <template v-for="(defect, index) in defectList">
                        <div
                          :key="index"
                          @click="selectDefect(defect)"
                          class="flex items-center px-6 py-3 hover:bg-gray-100 cursor-pointer rounded"
                        >
                          <div>
                            <div class="flex items-center justify-center">
                              <svg-vue
                                class="w-5 h-5 text-green-400"
                                icon="font-awesome.check-circle-solid"
                                v-if="currentDefects.includes(defect)"
                              ></svg-vue>
                              <div class="w-5 h-5 p-1 overflow-hidden rounded-full border" v-else></div>
                            </div>
                          </div>
                          <div class="flex flex-col ml-6">
                            <p class="text-sm leading-5 text-gray-700 group-hover:text-gray-900">
                              {{ defect.title }}
                            </p>
                            <p
                              class="text-xs leading-4 text-gray-900 group-hover:text-gray-700 group-focus:underline transition ease-in-out duration-150"
                            >
                              {{ $t('Required Time') }} :
                              {{ defect.required_time }}
                            </p>
                          </div>
                          <div class="ml-auto">
                            <small>{{ defect.price | currency }}</small>
                          </div>
                        </div>
                      </template>
                    </div>
                  </div>
                </div>
              </div>
            </template>
            <!-- defects list end -->
            <!-- priority list start -->
            <template v-if="priorityList.length && currentDefects.length">
              <div class="md:col-span-3">
                <div class="py-3">
                  <div class="border-t border-gray-200"></div>
                </div>
              </div>
              <div class="md:col-span-1">
                <h3 class="form-description-head">{{ $t('Select priority') }}</h3>
                <p class="form-description-text">{{ $t('Repair processing priority') }}.</p>
              </div>
              <div class="mt-5 md:mt-0 md:col-span-2">
                <div class="grid grid-cols-3 gap-3">
                  <div class="col-span-3">
                    <div :style="{ maxHeight: '200px' }" class="flex flex-col overflow-y-auto">
                      <template v-for="(priority, index) in priorityList">
                        <div
                          :key="index"
                          @click="selectPriority(priority)"
                          class="flex items-center px-6 py-3 hover:bg-gray-100 cursor-pointer rounded"
                        >
                          <div>
                            <div class="flex items-center justify-center">
                              <svg-vue
                                class="w-5 h-5 text-green-400"
                                icon="font-awesome.check-circle-solid"
                                v-if="repair.repair_priority_id === priority.id"
                              ></svg-vue>
                              <div class="w-5 h-5 p-1 overflow-hidden rounded-full border" v-else></div>
                            </div>
                          </div>
                          <div class="flex flex-col ml-6">
                            <p class="text-sm leading-5 text-gray-700 group-hover:text-gray-900">
                              {{ priority.name }}
                            </p>
                          </div>
                          <div class="ml-auto">
                            <small>{{ priority.extra_charge | currency }}</small>
                          </div>
                        </div>
                      </template>
                    </div>
                  </div>
                </div>
              </div>
            </template>
            <!-- priority list end -->
            <!-- technician list start -->
            <template v-if="technicians.length && currentDefects.length">
              <div class="md:col-span-3">
                <div class="py-3">
                  <div class="border-t border-gray-200"></div>
                </div>
              </div>
              <div class="md:col-span-1">
                <h3 class="form-description-head">{{ $t('Assign and payment') }}</h3>
                <p class="form-description-text">
                  {{ $t('Assign repair to technician,enter prepaid or advance amount.') }}
                </p>
              </div>
              <div class="mt-5 md:mt-0 md:col-span-2">
                <div class="grid grid-cols-3 gap-3">
                  <div class="col-span-3">
                    <div class="bg-white text-right px-4 py-3 sm:px-6">
                      <strong>{{ $t('Subtotal') }}</strong>
                      : {{ subTotalAmount | currency }}
                      <br />
                      <strong>{{ $t('Priority Process Charges') }}</strong>
                      : {{ currentPriority.extra_charge | currency }}
                      <br />
                      <strong>{{ $t('Tax') }}</strong>
                      : {{ taxAmount | currency }}
                      <br />
                      <strong>{{ $t('Total Amount') }}</strong>
                      : {{ totalAmountWithTax | currency }}
                    </div>
                  </div>
                  <div class="col-span-3">
                    <div :style="{ maxHeight: '200px' }" class="flex flex-col overflow-y-auto">
                      <template v-for="(technician, index) in technicians">
                        <div
                          :key="index"
                          @click="repair.user_id = technician.id"
                          class="flex items-center px-6 py-3 hover:bg-gray-100 cursor-pointer rounded"
                        >
                          <div>
                            <div class="flex items-center justify-center">
                              <svg-vue
                                class="w-5 h-5 text-green-400"
                                icon="font-awesome.check-circle-solid"
                                v-if="repair.user_id === technician.id"
                              ></svg-vue>
                              <div class="w-5 h-5 p-1 overflow-hidden rounded-full border" v-else></div>
                            </div>
                          </div>
                          <div class="flex flex-col ml-6">
                            <p class="text-sm leading-5 text-gray-700 group-hover:text-gray-900">
                              {{ technician.name }}({{ technician.email }})
                            </p>
                          </div>
                        </div>
                      </template>
                    </div>
                  </div>
                  <div class="col-span-3">
                    <label class="form-label" for="pre_paid">{{ $t('Advance or prepaid amount') }}</label>
                    <div class="pt-1">
                      <input
                        :placeholder="$t('Advance or prepaid amount')"
                        class="form-input"
                        id="pre_paid"
                        required
                        type="number"
                        v-model="repair.pre_paid"
                      />
                    </div>
                  </div>
                  <div class="col-span-3">
                    <label class="form-label" for="is_device_collected">{{ $t('Device collected status') }}</label>
                    <input-switch
                      :disabled-label="$t('The customer will be notified to dispatch device')"
                      :enabled-label="$t('The device has been just collected from customer')"
                      id="is_device_collected"
                      v-model="repair.is_device_collected"
                    ></input-switch>
                    <div class="form-description-text">
                      {{ $t('This option will control to notify customer to dispatch his device via email') }}.
                    </div>
                  </div>
                </div>
                <div class="col-span-3">
                  <label class="form-label" for="has_warranty">{{ $t('Device warranty status') }}</label>
                  <input-switch
                    :disabled-label="$t('No')"
                    :enabled-label="$t('Yes')"
                    id="has_warranty"
                    v-model="repair.has_warranty"
                  ></input-switch>
                  <div class="form-description-text">{{ $t('This option will set device has warranty or not') }}.</div>
                </div>
              </div>
            </template>
            <!-- priority list end -->
          </div>
          <template v-if="currentDefects.length">
            <div class="card-footer">
              <span class="inline-flex">
                <router-link class="btn btn-secondary shadow-sm rounded-md mr-4" to="/admin/repair-orders">{{ $t('Cancel') }}</router-link>
                <button class="btn btn-app shadow-sm rounded-md" type="submit">
                  {{ $t('Submit') }}
                </button>
              </span>
            </div>
          </template>
        </div>
      </div>
    </form>
  </main>
</template>
<script>
export default {
  name: 'new',
  data() {
    return {
      loading: false,
      technicians: [],
      priorityList: [],
      brandList: [],
      deviceList: [],
      defectList: [],
      currentDefects: [],
      currentDevice: null,
      currentBrand: null,
      currentPriority: {
        extra_charge: 0,
      },
      tax: {
        tax_rate: 0,
        is_tax_fix: false,
        is_tax_included: false,
      },
      repair: {
        defects: [],
        has_warranty: false,
        name: null,
        email: null,
        phone: null,
        address: null,
        serial_number: null,
        diagnostics: null,
        device_id: null,
        brand_id: null,
        repair_priority_id: 1,
        sub_total: 0,
        total_cost: 0,
        profit: 0,
        tax: 0,
        total_charges: 0,
        user_id: null,
        pre_paid: 0,
      },
    };
  },
  mounted() {
    this.getBrandAndPriorityList();
    this.getUsers();
  },
  computed: {
    repairCostAmount() {
      let amount = 0;
      this.currentDefects.forEach((defect) => {
        amount += defect.cost;
      });
      return amount;
    },
    subTotalAmount() {
      let amount = 0;
      this.currentDefects.forEach((defect) => {
        amount += defect.price;
      });
      return amount;
    },
    profit() {
      return Number(this.totalAmountWithTax - this.repairCostAmount - this.taxAmount);
    },
    subTotalWithPriority() {
      const amount = Number(this.subTotalAmount) + Number(this.currentPriority.extra_charge);
      return amount;
    },
    taxAmount() {
      const amount = this.subTotalWithPriority;
      const rate = Number(this.tax.tax_rate ?? this.tax.rate);
      if (this.tax.is_vat) {
        if (this.tax.is_tax_included) {
          const vatAmount = amount - amount / (1 + rate / 100);
          return vatAmount.toFixed(2);
        } else {
          const vatAmount = amount * (rate / 100);
          return vatAmount.toFixed(2);
        }
      } else {
        if (this.tax.is_tax_fix) {
          return rate;
        }
        return amount * (rate / 100);
      }
    },
    totalAmountWithTax() {
      if (this.tax.is_tax_included) {
        return this.subTotalWithPriority;
      }
      return this.subTotalWithPriority + Number(this.taxAmount);
    },
  },
  methods: {
    getUsers() {
      this.loading = true;
      axios
        .get(route('users.technicians'))
        .then((response) => {
          this.technicians = response.data.technicians;
          this.loading = false;
        })
        .catch(() => {
          this.loading = true;
        });
    },
    selectPriority(priority) {
      this.currentPriority = priority;
      this.repair.repair_priority_id = priority.id;
    },
    selectDefect(defect) {
      if (this.currentDefects.includes(defect)) {
        for (let i = 0; i < this.currentDefects.length; i++) {
          if (this.currentDefects[`${i}`] === defect) {
            this.currentDefects.splice(i, 1);
          }
        }
      } else {
        this.currentDefects.push(defect);
      }
    },
    submitRepair() {
      this.loading = true;
      this.repair.defects = this.currentDefects;
      this.repair.sub_total = this.subTotalAmount;
      this.repair.total_charges = this.totalAmountWithTax;
      this.repair.profit = this.profit;
      this.repair.total_cost = this.repairCostAmount;
      this.repair.tax = this.taxAmount;
      this.repair.payment_gateway_key = 'biller';
      this.repair.payment_gateway_value = this.$store.state.user.name;
      axios
        .post(route('repair-orders.store'), this.repair)
        .then((response) => {
          this.$notify({
            title: this.$i18n.t('Success').toString(),
            text: response.data.message.toString(),
            type: 'success',
          });
          this.loading = false;
          this.$router.push(`/admin/repair-orders/${response.data.order.uuid}/handle`);
        })
        .catch(() => {
          this.loading = false;
        });
    },
    getBrandAndPriorityList() {
      this.loading = true;
      this.defectList = [];
      this.deviceList = [];
      axios
        .get(route('repair.brand.list'))
        .then((response) => {
          this.priorityList = response.data.priorities;
          this.brandList = response.data.brands;
          this.getTax();
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
        });
    },
    getDevices(brand) {
      this.loading = true;
      this.repair.brand_id = brand.id;
      this.currentBrand = brand;
      this.defectList = [];
      axios
        .post(route('repair.device.list'), { brand_id: this.repair.brand_id })
        .then((response) => {
          this.loading = false;
          this.deviceList = response.data;
        })
        .catch(() => {
          this.loading = false;
        });
    },
    getTax() {
      axios
        .get(route('settings.get.tax'))
        .then((response) => {
          this.tax = response.data;
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
        });
    },
    getDefects(device) {
      this.loading = true;
      this.repair.device_id = device.id;
      this.currentDevice = device;
      axios
        .post(route('repair.defect.list'), {
          device_id: this.repair.device_id,
        })
        .then((response) => {
          this.defectList = response.data;
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
        });
    },
  },
};
</script>
